/**
 * Layered overlay host (feature `layered-overlays`).
 *
 * One host per document, mounted as the last child of `.ae-root` after the
 * application layout, so layout stacking contexts (transform, filter,
 * isolation…) can never lift content above overlays while overlays still see
 * the root's events and inherited styles. The host enforces a fixed order:
 *
 *   application < dialog < toast < immersive < modal
 *
 * Overlays keep their logical owner (the component that opened them) through
 * `setOverlayOwner`, which lets DOM path walkers such as the developer
 * inspector continue from the overlay back into the originating component tree.
 */

import { loadStyle } from './utils/css.js'

export const LAYERED_OVERLAYS_FEATURE = 'layered-overlays'

export type OverlayLayer = 'dialog' | 'toast' | 'immersive' | 'modal'

export const OVERLAY_LAYERS: readonly OverlayLayer[] = ['dialog', 'toast', 'immersive', 'modal']

export const LAYERED_OVERLAY_HOST_CLASS = 'ae-layered-overlays'
export const OVERLAY_LAYER_ATTRIBUTE = 'data-ae-overlay-layer'
export const OVERLAY_SCRIM_CLASS = 'ae-overlay-scrim'
export const OVERLAY_STACK_CLASS = 'ae-overlay-stack'
export const OVERLAY_POPUPS_CLASS = 'ae-overlay-popups'

const STACK_LAYERS = new Set<OverlayLayer>(['dialog', 'immersive', 'modal'])
const HOST_REFERENCES = new WeakMap<Document, number>()
const VIEWPORT_CLEANUPS = new WeakMap<HTMLElement, () => void>()
const OVERLAY_OWNERS = new WeakMap<Node, Node>()

const LAYERED_OVERLAY_CSS = `
.${LAYERED_OVERLAY_HOST_CLASS} {
  position: fixed;
  inset: 0;
  z-index: var(--ae-overlay-host-z-index, 1000);
  isolation: isolate;
  pointer-events: none;
  overflow: visible;
}

.${LAYERED_OVERLAY_HOST_CLASS} > [${OVERLAY_LAYER_ATTRIBUTE}] {
  position: absolute;
  inset: 0;
  isolation: isolate;
  pointer-events: none;
}

.${LAYERED_OVERLAY_HOST_CLASS} > [${OVERLAY_LAYER_ATTRIBUTE}="dialog"] { z-index: 100; }
.${LAYERED_OVERLAY_HOST_CLASS} > [${OVERLAY_LAYER_ATTRIBUTE}="toast"] { z-index: 200; }
.${LAYERED_OVERLAY_HOST_CLASS} > [${OVERLAY_LAYER_ATTRIBUTE}="immersive"] { z-index: 300; }
.${LAYERED_OVERLAY_HOST_CLASS} > [${OVERLAY_LAYER_ATTRIBUTE}="modal"] { z-index: 400; }

.${OVERLAY_SCRIM_CLASS} {
  position: absolute;
  inset: 0;
  background: var(--ae-dialog-scrim-color, var(--ae-dialog-dock-container-bg-color, rgba(20, 27, 39, 0.36)));
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.18s ease;
}

.${OVERLAY_SCRIM_CLASS}[data-ae-scrim-visible="true"] {
  opacity: 1;
  pointer-events: auto;
}

.${OVERLAY_STACK_CLASS},
.${OVERLAY_POPUPS_CLASS} {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.${OVERLAY_STACK_CLASS} > .ae-dialog__container {
  pointer-events: none;
}

.${OVERLAY_STACK_CLASS} > .ae-dialog__container:not(.ae-dialog__backdrop--appearance-immersive) {
  background: transparent !important;
  background-color: transparent !important;
}

.${OVERLAY_STACK_CLASS} > .ae-dialog__container[data-ae-stack-state="active"] > .ae-dialog {
  pointer-events: auto;
}

.${OVERLAY_STACK_CLASS} > .ae-dialog__container[data-ae-stack-transition="collapsing"] {
  opacity: 0;
}

.${OVERLAY_STACK_CLASS} > .ae-dialog__container[hidden],
.${OVERLAY_STACK_CLASS} > .ae-dialog-stack-placeholder[hidden] {
  display: none !important;
}

.ae-dialog-stack-placeholder {
  pointer-events: none !important;
  background: transparent !important;
}

.ae-dialog-stack-placeholder > .ae-dialog-stack-placeholder__surface {
  opacity: var(--ae-dialog-stack-placeholder-opacity, 0.55);
  filter: saturate(0.6);
  pointer-events: none !important;
}

.ae-dialog__swipe-handle { display: none; }

/* Sheets share the desktop stack and lifecycle, including hidden view state. */
@media (max-width: 720px), (max-width: 1000px) and (max-height: 560px) {
  .${LAYERED_OVERLAY_HOST_CLASS} {
    top: var(--ae-overlay-viewport-top, 0px);
    height: var(--ae-overlay-viewport-height, 100dvh);
    bottom: auto;
  }

  .${OVERLAY_STACK_CLASS} > .ae-dialog__container[data-ae-stack-type] {
    position: absolute !important;
    inset: 0 !important;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 0;
    overflow: hidden;
  }

  .${OVERLAY_STACK_CLASS} > .ae-dialog__container[data-ae-stack-type="drillInto"] {
    align-items: flex-end;
  }

  .${OVERLAY_STACK_CLASS} > .ae-dialog__container[data-ae-stack-type] > .ae-dialog:not(.ae-dialog--appearance-immersive) {
    position: relative !important;
    inset: auto !important;
    box-sizing: border-box;
    display: grid;
    grid-template: "header" auto "body" minmax(0, 1fr) "commands" auto / minmax(0, 1fr);
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    min-height: 0 !important;
    height: auto !important;
    max-height: calc(100% - max(16px, env(safe-area-inset-bottom))) !important;
    margin: 0 !important;
    padding-top: env(safe-area-inset-top);
    padding-bottom: max(44px, env(safe-area-inset-bottom));
    border-radius: 0 0 18px 18px;
    overflow: hidden;
    transform: translateY(calc(var(--ae-dialog-stack-depth, 0) * 12px)) scale(calc(1 - var(--ae-dialog-stack-depth, 0) * 0.025));
    transform-origin: top center;
    translate: 0 var(--ae-sheet-drag, 0px);
    animation: ae-sheet-from-top 220ms ease-out;
  }

  .${OVERLAY_STACK_CLASS} > .ae-dialog__container[data-ae-stack-type="drillInto"] > .ae-dialog:not(.ae-dialog--appearance-immersive) {
    height: calc(100% - max(20px, env(safe-area-inset-top))) !important;
    max-height: calc(100% - max(20px, env(safe-area-inset-top))) !important;
    border-radius: 18px 18px 0 0;
    padding-top: 44px;
    padding-bottom: env(safe-area-inset-bottom);
    transform: translateY(calc(var(--ae-dialog-stack-depth, 0) * -12px)) scale(calc(1 - var(--ae-dialog-stack-depth, 0) * 0.025));
    transform-origin: bottom center;
    animation-name: ae-sheet-from-bottom;
  }

  .${LAYERED_OVERLAY_HOST_CLASS} .ae-dialog__body {
    min-width: 0;
    min-height: 0;
    overflow: auto;
    overscroll-behavior: contain;
  }

  .${LAYERED_OVERLAY_HOST_CLASS} .ae-dialog__commands,
  .${LAYERED_OVERLAY_HOST_CLASS} .ae-command-dialog__commands {
    flex-wrap: wrap;
  }

  .${LAYERED_OVERLAY_HOST_CLASS} .ae-dialog__commands-bar {
    padding-bottom: env(safe-area-inset-bottom);
  }

  .${LAYERED_OVERLAY_HOST_CLASS} .ae-dialog button { min-height: 44px; }
  .${LAYERED_OVERLAY_HOST_CLASS} .ae-command-dialog__close,
  .${LAYERED_OVERLAY_HOST_CLASS} .ae-command-dialog__action-btn { min-width: 44px; }
  .${LAYERED_OVERLAY_HOST_CLASS} .ae-dialog input,
  .${LAYERED_OVERLAY_HOST_CLASS} .ae-dialog textarea,
  .${LAYERED_OVERLAY_HOST_CLASS} .ae-dialog select { font-size: max(16px, 1em); }

  .${LAYERED_OVERLAY_HOST_CLASS} .ae-dialog__swipe-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: auto;
    bottom: 0;
    left: calc(50% - 32px);
    width: 64px;
    height: 44px;
    min-height: 44px;
    padding: 8px 0;
    border: 0;
    background: transparent;
    color: inherit;
    touch-action: none;
    cursor: grab;
    z-index: 1;
  }
  [data-ae-stack-type="drillInto"] .ae-dialog__swipe-handle { top: 0; bottom: auto; }
  .ae-dialog__swipe-handle > span {
    width: 32px;
    height: 4px;
    border-radius: 4px;
    background: var(--md-sys-color-outline, #808692);
  }
}

@keyframes ae-sheet-from-bottom {
  from { translate: 0 100%; }
  to { translate: 0 0; }
}
@keyframes ae-sheet-from-top {
  from { translate: 0 -100%; }
  to { translate: 0 0; }
}
@media (prefers-reduced-motion: reduce) {
  .${LAYERED_OVERLAY_HOST_CLASS} .ae-dialog { animation: none !important; transition: none !important; }
  .${OVERLAY_SCRIM_CLASS} { transition: none; }
}

.${OVERLAY_POPUPS_CLASS} > * {
  pointer-events: auto;
}
`

export type LayeredOverlayHost = {
  host: HTMLElement
  layer(layer: OverlayLayer): HTMLElement
}

/** Acquire the document's overlay host; balanced by `releaseLayeredOverlayHost`. */
export function acquireLayeredOverlayHost(ownerDocument: Document) {
  HOST_REFERENCES.set(ownerDocument, (HOST_REFERENCES.get(ownerDocument) ?? 0) + 1)

  return getOrCreateLayeredOverlayHost(ownerDocument)
}

/** Release a reference; the host is removed when no owner and no overlay remains. */
export function releaseLayeredOverlayHost(ownerDocument: Document) {
  const references = Math.max(0, (HOST_REFERENCES.get(ownerDocument) ?? 0) - 1)
  HOST_REFERENCES.set(ownerDocument, references)

  const host = findLayeredOverlayHost(ownerDocument)

  if (references === 0 && host && !hasMountedOverlays(host)) {
    VIEWPORT_CLEANUPS.get(host)?.()
    VIEWPORT_CLEANUPS.delete(host)
    host.remove()
  }
}

export function findLayeredOverlayHost(ownerDocument?: Document | null): HTMLElement | null {
  const resolvedDocument = ownerDocument || (typeof document === 'object' ? document : null)

  return resolvedDocument?.querySelector?.(`.${LAYERED_OVERLAY_HOST_CLASS}[data-ae-overlay-host="layered"]`) as HTMLElement | null ?? null
}

export function getOrCreateLayeredOverlayHost(ownerDocument: Document): LayeredOverlayHost {
  loadStyle(LAYERED_OVERLAY_CSS, 'ae-layered-overlays')

  let host = findLayeredOverlayHost(ownerDocument)

  if (!host) {
    host = ownerDocument.createElement('div')
    host.classList.add(LAYERED_OVERLAY_HOST_CLASS)
    host.dataset.aeOverlayHost = 'layered'

    for (const layer of OVERLAY_LAYERS) {
      host.appendChild(createLayer(ownerDocument, layer))
    }

    // Last child of the application root, after its layout: overlays see the root's events and
    // inherited styles, while staying outside the layout's stacking contexts.
    const root = ownerDocument.querySelector('.ae-root') || ownerDocument.body
    root.appendChild(host)
    observeVisualViewport(host)
  }

  const resolvedHost = host

  return {
    host: resolvedHost,
    layer: (layer: OverlayLayer) => findLayerChild(resolvedHost, layer),
  }
}

/** Keep sheets above the software keyboard and within the visible viewport. */
function observeVisualViewport(host: HTMLElement) {
  const viewport = host.ownerDocument.defaultView?.visualViewport
  if (!viewport) {
    return
  }
  const update = () => {
    host.style.setProperty('--ae-overlay-viewport-top', `${viewport.offsetTop}px`)
    host.style.setProperty('--ae-overlay-viewport-height', `${viewport.height}px`)
  }
  viewport.addEventListener('resize', update)
  viewport.addEventListener('scroll', update)
  update()
  VIEWPORT_CLEANUPS.set(host, () => {
    viewport.removeEventListener('resize', update)
    viewport.removeEventListener('scroll', update)
  })
}

/** The layer element when the layered host exists, otherwise `null` (legacy rendering). */
export function findOverlayLayer(ownerDocument: Document | null | undefined, layer: OverlayLayer) {
  return findLayerChild(findLayeredOverlayHost(ownerDocument), layer)
}

export function getLayerStackElement(layerElement: HTMLElement) {
  return findChild(layerElement, child => child.classList.contains(OVERLAY_STACK_CLASS))
}

export function getLayerScrimElement(layerElement: HTMLElement) {
  return findChild(layerElement, child => child.classList.contains(OVERLAY_SCRIM_CLASS))
}

/**
 * Popups mount above the stack of the layer their target lives in so a popup
 * opened from a confirmation stays above it, and above dialogs otherwise.
 */
export function findLayeredPopupHost(target: Element | null | undefined, ownerDocument: Document | null | undefined) {
  const host = findLayeredOverlayHost(ownerDocument)

  if (!host) {
    return null
  }

  const targetLayer = target?.closest?.(`[${OVERLAY_LAYER_ATTRIBUTE}]`) as HTMLElement | null
  const layerElement = targetLayer && host.contains(targetLayer) && STACK_LAYERS.has(targetLayer.getAttribute(OVERLAY_LAYER_ATTRIBUTE) as OverlayLayer)
    ? targetLayer
    : findLayerChild(host, 'dialog')

  return findChild(layerElement, child => child.classList.contains(OVERLAY_POPUPS_CLASS))
}

/** Record the logical owner of an element that was moved into an overlay layer. */
export function setOverlayOwner(element: Node | null | undefined, owner: Node | null | undefined) {
  if (!element) {
    return
  }

  if (owner) {
    OVERLAY_OWNERS.set(element, owner)
  } else {
    OVERLAY_OWNERS.delete(element)
  }
}

export function getOverlayOwner(element: Node | null | undefined) {
  return element ? OVERLAY_OWNERS.get(element) ?? null : null
}

/** Parent for DOM path walks: follows overlay owner links before the physical parent. */
export function getLogicalParentNode(node: Node) {
  return getOverlayOwner(node) || node.parentNode
}

/** Element counterpart of `getLogicalParentNode` for ancestor walks that only inspect elements. */
export function getLogicalParentElement(element: Element): HTMLElement | null {
  const parent = getLogicalParentNode(element)
  return parent && parent.nodeType === 1 ? parent as HTMLElement : null
}

function findChild(parent: Element | null | undefined, predicate: (child: HTMLElement) => boolean): HTMLElement | null {
  for (const child of Array.from(parent?.children || [])) {
    if (predicate(child as HTMLElement)) {
      return child as HTMLElement
    }
  }

  return null
}

function findLayerChild(host: Element | null | undefined, layer: OverlayLayer) {
  return findChild(host, child => child.getAttribute(OVERLAY_LAYER_ATTRIBUTE) === layer)
}

function createLayer(ownerDocument: Document, layer: OverlayLayer) {
  const element = ownerDocument.createElement('div')
  element.className = `ae-overlay-layer ae-overlay-layer--${layer}`
  element.setAttribute(OVERLAY_LAYER_ATTRIBUTE, layer)

  if (STACK_LAYERS.has(layer)) {
    const scrim = ownerDocument.createElement('div')
    scrim.className = OVERLAY_SCRIM_CLASS
    scrim.setAttribute('aria-hidden', 'true')
    scrim.dataset.aeScrimVisible = 'false'

    const stack = ownerDocument.createElement('div')
    stack.className = OVERLAY_STACK_CLASS

    const popups = ownerDocument.createElement('div')
    popups.className = OVERLAY_POPUPS_CLASS

    element.append(scrim, stack, popups)
  }

  return element
}

function hasMountedOverlays(host: HTMLElement) {
  return !!host.querySelector(`.${OVERLAY_STACK_CLASS} > .ae-dialog__container, .${OVERLAY_POPUPS_CLASS} > *, [${OVERLAY_LAYER_ATTRIBUTE}="toast"] > *`)
}
